import type { PostgrestError, RealtimeChannel } from '@supabase/supabase-js';
import type { ExternalOrderLinks, QrPaymentMode, ReservationStatus, Role } from '@adop/shared';
import { getSupabase } from './supabase';
import type { Database, Json } from './database.types';
import { planMenuCopy, type MenuCopyScope, type MenuSnapshot } from './menu';

type Tables = Database['public']['Tables'];

export type LocationRow = Tables['locations']['Row'];
export type RestaurantRow = Tables['restaurants']['Row'];
export type MembershipRow = Tables['memberships']['Row'];
export type ProfileRow = Tables['profiles']['Row'];
export type OpeningHoursRow = Tables['opening_hours']['Row'];
export type SpecialHoursRow = Tables['special_hours']['Row'];
export type MenuCategoryRow = Tables['menu_categories']['Row'];
export type MenuItemRow = Tables['menu_items']['Row'];
export type ModifierGroupRow = Tables['modifier_groups']['Row'];
export type ModifierOptionRow = Tables['modifier_options']['Row'];
export type MenuItemModifierGroupRow = Tables['menu_item_modifier_groups']['Row'];
export type OrderRow = Tables['orders']['Row'];
export type OrderItemRow = Tables['order_items']['Row'];
export type LocationTableRow = Tables['location_tables']['Row'];
export type LocationTableUpdate = Tables['location_tables']['Update'];
export type ReservationRow = Tables['reservations']['Row'];
export type ReservationSettingsRow = Tables['reservation_settings']['Row'];
export type PrinterDeviceRow = Tables['printer_devices']['Row'];
export type PrintJobRow = Tables['print_jobs']['Row'];
export type AuditLogRow = Tables['audit_log']['Row'];

export type LocationUpdate = Tables['locations']['Update'];
export type MenuItemInsert = Tables['menu_items']['Insert'];
export type MenuItemUpdate = Tables['menu_items']['Update'];
export type ModifierGroupInsert = Tables['modifier_groups']['Insert'];
export type ModifierOptionInsert = Tables['modifier_options']['Insert'];

interface ResultLike<T> {
  data: T | null;
  error: PostgrestError | null;
}

function unwrap<T>(result: ResultLike<T>): T {
  if (result.error) throw new Error(result.error.message);
  if (result.data === null) throw new Error('Not found.');
  return result.data;
}

function unwrapMaybe<T>(result: ResultLike<T>): T | null {
  if (result.error) throw new Error(result.error.message);
  return result.data;
}

function asJson(value: unknown): Json {
  return value as Json;
}

/** Common fields every menu row needs. */
interface TenantScope {
  restaurantId: string;
  locationId: string;
}

// ---------------------------------------------------------------------------
// Profile / tenancy
// ---------------------------------------------------------------------------

export async function getProfile(userId: string): Promise<ProfileRow | null> {
  return unwrapMaybe(await getSupabase().from('profiles').select('*').eq('id', userId).maybeSingle());
}

export async function updateProfile(userId: string, patch: { full_name?: string | null }): Promise<void> {
  const { error } = await getSupabase().from('profiles').update(patch).eq('id', userId);
  if (error) throw new Error(error.message);
}

export async function listMemberships(restaurantId: string): Promise<MembershipRow[]> {
  return unwrap(await getSupabase().from('memberships').select('*').eq('restaurant_id', restaurantId));
}

export async function updateMembership(
  restaurantId: string,
  userId: string,
  patch: { role?: Role; location_ids?: string[] | null },
): Promise<void> {
  const { error } = await getSupabase()
    .from('memberships')
    .update(patch)
    .eq('restaurant_id', restaurantId)
    .eq('user_id', userId);
  if (error) throw new Error(membershipErrorMessage(error));
}

/**
 * The last-owner trigger raises errcode 23514 with a terse message. Translate it
 * into an actionable sentence so the Team page can show something useful.
 */
function membershipErrorMessage(error: PostgrestError): string {
  if (error.code === '23514') {
    return 'A restaurant must keep at least one owner. Promote another member to owner first.';
  }
  return error.message;
}

export async function removeMembership(restaurantId: string, userId: string): Promise<void> {
  const { error } = await getSupabase()
    .from('memberships')
    .delete()
    .eq('restaurant_id', restaurantId)
    .eq('user_id', userId);
  if (error) throw new Error(error.message);
}

export async function listProfilesByIds(ids: string[]): Promise<ProfileRow[]> {
  if (ids.length === 0) return [];
  return unwrap(await getSupabase().from('profiles').select('*').in('id', ids));
}

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

export async function listLocations(restaurantId: string): Promise<LocationRow[]> {
  return unwrap(
    await getSupabase()
      .from('locations')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('name', { ascending: true }),
  );
}

export async function getLocation(locationId: string): Promise<LocationRow | null> {
  return unwrapMaybe(await getSupabase().from('locations').select('*').eq('id', locationId).maybeSingle());
}

export interface NewLocationInput {
  restaurantId: string;
  name: string;
  slug: string;
  timezone: string;
  address?: Record<string, string>;
  phone?: string;
  email?: string;
}

export async function insertLocation(input: NewLocationInput): Promise<LocationRow> {
  return unwrap(
    await getSupabase()
      .from('locations')
      .insert({
        restaurant_id: input.restaurantId,
        name: input.name,
        slug: input.slug,
        timezone: input.timezone,
        address: input.address ? asJson(input.address) : null,
        phone: input.phone ?? null,
        email: input.email ?? null,
      })
      .select('*')
      .single(),
  );
}

export async function updateLocation(locationId: string, patch: LocationUpdate): Promise<void> {
  const { error } = await getSupabase().from('locations').update(patch).eq('id', locationId);
  if (error) throw new Error(error.message);
}

/** Branding/about are JSON columns; keep the cast in one place. */
export async function updateLocationBranding(
  locationId: string,
  branding: unknown,
  about: string | null,
): Promise<void> {
  await updateLocation(locationId, { branding: asJson(branding), about });
}

export async function updateLocationOrdering(locationId: string, enabled: boolean): Promise<void> {
  await updateLocation(locationId, { ordering_enabled: enabled });
}

export async function setLocationArchived(locationId: string, archived: boolean): Promise<void> {
  await updateLocation(locationId, { archived_at: archived ? new Date().toISOString() : null });
}

/** SEO / AI-visibility fields plus the QR ordering switches, all owner-managed. */
export interface LocationSeoPatch {
  external_order_links?: ExternalOrderLinks;
  google_review_url?: string | null;
  cuisines?: string[];
  price_range?: string | null;
  qr_ordering_enabled?: boolean;
  qr_payment_mode?: QrPaymentMode;
}

export async function updateLocationSeo(locationId: string, patch: LocationSeoPatch): Promise<void> {
  const update: LocationUpdate = {};
  if (patch.external_order_links !== undefined) {
    update.external_order_links = asJson(patch.external_order_links);
  }
  if (patch.google_review_url !== undefined) update.google_review_url = patch.google_review_url;
  if (patch.cuisines !== undefined) update.cuisines = patch.cuisines;
  if (patch.price_range !== undefined) update.price_range = patch.price_range;
  if (patch.qr_ordering_enabled !== undefined) update.qr_ordering_enabled = patch.qr_ordering_enabled;
  if (patch.qr_payment_mode !== undefined) update.qr_payment_mode = patch.qr_payment_mode;
  if (Object.keys(update).length === 0) return;
  await updateLocation(locationId, update);
}

// ---------------------------------------------------------------------------
// Tables (QR ordering)
// ---------------------------------------------------------------------------

function tableErrorMessage(error: PostgrestError): string {
  if (error.code === '23505') {
    return 'A table with that label already exists at this location.';
  }
  return error.message;
}

export async function listTables(locationId: string): Promise<LocationTableRow[]> {
  return unwrap(
    await getSupabase()
      .from('location_tables')
      .select('*')
      .eq('location_id', locationId)
      .order('label', { ascending: true }),
  );
}

export interface NewTableInput {
  restaurantId: string;
  locationId: string;
  label: string;
  seats: number | null;
  qrToken: string;
}

export async function createTable(input: NewTableInput): Promise<LocationTableRow> {
  const { data, error } = await getSupabase()
    .from('location_tables')
    .insert({
      restaurant_id: input.restaurantId,
      location_id: input.locationId,
      label: input.label,
      seats: input.seats,
      qr_token: input.qrToken,
    })
    .select('*')
    .single();
  if (error) throw new Error(tableErrorMessage(error));
  if (!data) throw new Error('Not found.');
  return data;
}

export async function updateTable(id: string, patch: LocationTableUpdate): Promise<void> {
  const { error } = await getSupabase().from('location_tables').update(patch).eq('id', id);
  if (error) throw new Error(tableErrorMessage(error));
}

export async function deleteTable(id: string): Promise<void> {
  const { error } = await getSupabase().from('location_tables').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Hours
// ---------------------------------------------------------------------------

export async function listOpeningHours(locationId: string): Promise<OpeningHoursRow[]> {
  return unwrap(
    await getSupabase()
      .from('opening_hours')
      .select('*')
      .eq('location_id', locationId)
      .order('weekday', { ascending: true })
      .order('opens', { ascending: true }),
  );
}

export interface HoursRange {
  weekday: number;
  opens: string;
  closes: string;
}

export async function replaceOpeningHours(scope: TenantScope, ranges: HoursRange[]): Promise<void> {
  const { error: deleteError } = await getSupabase()
    .from('opening_hours')
    .delete()
    .eq('location_id', scope.locationId);
  if (deleteError) throw new Error(deleteError.message);
  if (ranges.length === 0) return;
  const { error } = await getSupabase().from('opening_hours').insert(
    ranges.map((range) => ({
      restaurant_id: scope.restaurantId,
      location_id: scope.locationId,
      weekday: range.weekday,
      opens: range.opens,
      closes: range.closes,
    })),
  );
  if (error) throw new Error(error.message);
}

export async function listSpecialHours(locationId: string): Promise<SpecialHoursRow[]> {
  return unwrap(
    await getSupabase()
      .from('special_hours')
      .select('*')
      .eq('location_id', locationId)
      .order('date', { ascending: true }),
  );
}

export async function upsertSpecialHours(
  scope: TenantScope,
  input: {
    id?: string;
    date: string;
    closed: boolean;
    opens: string | null;
    closes: string | null;
    note: string | null;
  },
): Promise<void> {
  const row = {
    restaurant_id: scope.restaurantId,
    location_id: scope.locationId,
    date: input.date,
    closed: input.closed,
    opens: input.opens,
    closes: input.closes,
    note: input.note,
  };
  const { error } = input.id
    ? await getSupabase().from('special_hours').update(row).eq('id', input.id)
    : await getSupabase().from('special_hours').insert(row);
  if (error) throw new Error(error.message);
}

export async function deleteSpecialHours(id: string): Promise<void> {
  const { error } = await getSupabase().from('special_hours').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Menu
// ---------------------------------------------------------------------------

export async function fetchMenu(locationId: string): Promise<MenuSnapshot> {
  const [categories, items, groups, options, links] = await Promise.all([
    getSupabase().from('menu_categories').select('*').eq('location_id', locationId).order('sort_order'),
    getSupabase().from('menu_items').select('*').eq('location_id', locationId).order('sort_order'),
    getSupabase().from('modifier_groups').select('*').eq('location_id', locationId).order('created_at'),
    getSupabase()
      .from('modifier_options')
      .select('*')
      .eq('location_id', locationId)
      .order('sort_order'),
    getSupabase().from('menu_item_modifier_groups').select('*').eq('location_id', locationId),
  ]);
  return {
    categories: unwrap(categories),
    items: unwrap(items),
    groups: unwrap(groups),
    options: unwrap(options),
    links: unwrap(links),
  };
}

export async function insertMenuCategory(
  scope: TenantScope,
  input: { name: string; sort_order: number; available?: boolean },
): Promise<void> {
  const { error } = await getSupabase().from('menu_categories').insert({
    restaurant_id: scope.restaurantId,
    location_id: scope.locationId,
    name: input.name,
    sort_order: input.sort_order,
    available: input.available ?? true,
  });
  if (error) throw new Error(error.message);
}

export async function updateMenuCategory(
  id: string,
  patch: Tables['menu_categories']['Update'],
): Promise<void> {
  const { error } = await getSupabase().from('menu_categories').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteMenuCategory(id: string): Promise<void> {
  const { error } = await getSupabase().from('menu_categories').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function insertMenuItem(input: MenuItemInsert): Promise<void> {
  const { error } = await getSupabase().from('menu_items').insert(input);
  if (error) throw new Error(error.message);
}

export async function updateMenuItem(id: string, patch: MenuItemUpdate): Promise<void> {
  const { error } = await getSupabase().from('menu_items').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteMenuItem(id: string): Promise<void> {
  const { error } = await getSupabase().from('menu_items').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function insertModifierGroup(input: ModifierGroupInsert): Promise<void> {
  const { error } = await getSupabase().from('modifier_groups').insert(input);
  if (error) throw new Error(error.message);
}

export async function updateModifierGroup(
  id: string,
  patch: Tables['modifier_groups']['Update'],
): Promise<void> {
  const { error } = await getSupabase().from('modifier_groups').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteModifierGroup(id: string): Promise<void> {
  const { error } = await getSupabase().from('modifier_groups').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function insertModifierOption(input: ModifierOptionInsert): Promise<void> {
  const { error } = await getSupabase().from('modifier_options').insert(input);
  if (error) throw new Error(error.message);
}

export async function updateModifierOption(
  id: string,
  patch: Tables['modifier_options']['Update'],
): Promise<void> {
  const { error } = await getSupabase().from('modifier_options').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteModifierOption(id: string): Promise<void> {
  const { error } = await getSupabase().from('modifier_options').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/** Replaces the ordered set of modifier groups attached to an item. */
export async function replaceItemModifierGroups(
  scope: TenantScope,
  itemId: string,
  groupIds: string[],
): Promise<void> {
  const { error: deleteError } = await getSupabase()
    .from('menu_item_modifier_groups')
    .delete()
    .eq('item_id', itemId);
  if (deleteError) throw new Error(deleteError.message);
  if (groupIds.length === 0) return;
  const { error } = await getSupabase().from('menu_item_modifier_groups').insert(
    groupIds.map((groupId, index) => ({
      item_id: itemId,
      group_id: groupId,
      restaurant_id: scope.restaurantId,
      location_id: scope.locationId,
      sort_order: index,
    })),
  );
  if (error) throw new Error(error.message);
}

/**
 * Copies a menu between two locations of the same restaurant. Ids are remapped
 * with {@link planMenuCopy}; order, availability, images and tags are preserved.
 */
export async function copyMenu(
  fromLocationId: string,
  toLocationId: string,
  scope: MenuCopyScope = { categories: true, items: true, modifiers: true },
): Promise<MenuSnapshot> {
  if (fromLocationId === toLocationId) throw new Error('Pick a different location to copy from.');
  const target = await getLocation(toLocationId);
  if (!target) throw new Error('The destination location could not be found.');
  const source = await fetchMenu(fromLocationId);
  const plan = planMenuCopy(
    source,
    { restaurantId: target.restaurant_id, locationId: target.id },
    scope,
    () => crypto.randomUUID(),
  );

  if (plan.categories.length > 0) {
    const { error } = await getSupabase().from('menu_categories').insert(plan.categories);
    if (error) throw new Error(error.message);
  }
  if (plan.groups.length > 0) {
    const { error } = await getSupabase().from('modifier_groups').insert(plan.groups);
    if (error) throw new Error(error.message);
  }
  if (plan.options.length > 0) {
    const { error } = await getSupabase().from('modifier_options').insert(plan.options);
    if (error) throw new Error(error.message);
  }
  if (plan.items.length > 0) {
    const { error } = await getSupabase().from('menu_items').insert(plan.items);
    if (error) throw new Error(error.message);
  }
  if (plan.links.length > 0) {
    const { error } = await getSupabase().from('menu_item_modifier_groups').insert(plan.links);
    if (error) throw new Error(error.message);
  }
  return fetchMenu(toLocationId);
}

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

export async function listOrders(locationId: string, limit = 100): Promise<OrderRow[]> {
  return unwrap(
    await getSupabase()
      .from('orders')
      .select('*')
      .eq('location_id', locationId)
      .order('created_at', { ascending: false })
      .limit(limit),
  );
}

export async function listOrderItems(orderId: string): Promise<OrderItemRow[]> {
  return unwrap(await getSupabase().from('order_items').select('*').eq('order_id', orderId));
}

/** Item rows for a small set of orders (the live table board). */
export async function listOrderItemsByOrderIds(orderIds: string[]): Promise<OrderItemRow[]> {
  if (orderIds.length === 0) return [];
  return unwrap(await getSupabase().from('order_items').select('*').in('order_id', orderIds));
}

/** All order lines for a location, newest first (stats aggregation). */
export async function listOrderItemsByLocation(
  locationId: string,
  limit = 2000,
): Promise<OrderItemRow[]> {
  return unwrap(
    await getSupabase()
      .from('order_items')
      .select('*')
      .eq('location_id', locationId)
      .order('created_at', { ascending: false })
      .limit(limit),
  );
}

export async function updateOrderStatus(
  orderId: string,
  status: string,
  patch: { cancelled_reason?: string | null } = {},
): Promise<void> {
  const { error } = await getSupabase()
    .from('orders')
    .update({ status, ...patch })
    .eq('id', orderId);
  if (error) throw new Error(error.message);
}

export async function countOrdersToday(
  locationId: string,
  dayStartIso: string,
): Promise<{ count: number; revenueCents: number }> {
  const { data, error } = await getSupabase()
    .from('orders')
    .select('total_cents, status')
    .eq('location_id', locationId)
    .gte('created_at', dayStartIso);
  if (error) throw new Error(error.message);
  const rows = data ?? [];
  const revenueCents = rows
    .filter((row) => row.status !== 'cancelled' && row.status !== 'refunded')
    .reduce((sum, row) => sum + row.total_cents, 0);
  return { count: rows.length, revenueCents };
}

/** First instant of the current calendar month (local time), as ISO. */
export function monthStartIso(now: Date = new Date()): string {
  return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).toISOString();
}

/** Orders created this calendar month across the whole restaurant. */
export async function monthlyOrderCount(
  restaurantId: string,
  now: Date = new Date(),
): Promise<number> {
  const { count, error } = await getSupabase()
    .from('orders')
    .select('id', { count: 'exact', head: true })
    .eq('restaurant_id', restaurantId)
    .gte('created_at', monthStartIso(now));
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/**
 * Flip an unpaid order to paid through the security-definer RPC. Dashboard
 * updates cannot touch payment_status directly, so this is the only path.
 */
export async function markOrderPaid(orderId: string): Promise<void> {
  const { error } = await getSupabase().rpc('staff_mark_order_paid', { p_order_id: orderId });
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Reservations
// ---------------------------------------------------------------------------

export async function listReservations(locationId: string, limit = 200): Promise<ReservationRow[]> {
  return unwrap(
    await getSupabase()
      .from('reservations')
      .select('*')
      .eq('location_id', locationId)
      .order('requested_for', { ascending: false })
      .limit(limit),
  );
}

export async function updateReservation(
  id: string,
  patch: { status?: ReservationStatus; decline_reason?: string | null },
): Promise<void> {
  const { error } = await getSupabase().from('reservations').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function getReservationSettings(
  locationId: string,
): Promise<ReservationSettingsRow | null> {
  return unwrapMaybe(
    await getSupabase().from('reservation_settings').select('*').eq('location_id', locationId).maybeSingle(),
  );
}

export async function saveReservationSettings(
  scope: TenantScope,
  patch: Partial<Omit<ReservationSettingsRow, 'location_id' | 'restaurant_id'>>,
): Promise<void> {
  // A PostgREST upsert rewrites every column, including location_id and
  // restaurant_id, which requires table-level UPDATE. Authenticated users only
  // hold column grants, so an upsert fails with 42501. Update the granted
  // columns first, then insert a fresh row when none exists yet.
  const { data, error } = await getSupabase()
    .from('reservation_settings')
    .update(patch)
    .eq('location_id', scope.locationId)
    .select('location_id');
  if (error) throw new Error(error.message);
  if (data && data.length > 0) return;

  const { error: insertError } = await getSupabase().from('reservation_settings').insert({
    location_id: scope.locationId,
    restaurant_id: scope.restaurantId,
    ...patch,
  });
  if (insertError) throw new Error(insertError.message);
}

// ---------------------------------------------------------------------------
// Printers & print jobs
// ---------------------------------------------------------------------------

export async function listPrinterDevices(locationId: string): Promise<PrinterDeviceRow[]> {
  return unwrap(
    await getSupabase()
      .from('printer_devices')
      .select('*')
      .eq('location_id', locationId)
      .order('created_at', { ascending: true }),
  );
}

export async function renamePrinterDevice(id: string, name: string): Promise<void> {
  const { error } = await getSupabase().from('printer_devices').update({ name }).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function revokePrinterDevice(id: string): Promise<void> {
  const { error } = await getSupabase()
    .from('printer_devices')
    .update({ revoked_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw new Error(error.message);
}

export interface PrintJobInput extends TenantScope {
  deviceId: string;
  kind: 'order' | 'reservation' | 'test';
  orderId?: string | null;
  reservationId?: string | null;
}

/** Dashboard reprints/test prints: the DB forces status='queued' and validates the device. */
export async function insertPrintJob(input: PrintJobInput): Promise<void> {
  const { error } = await getSupabase().from('print_jobs').insert({
    restaurant_id: input.restaurantId,
    location_id: input.locationId,
    device_id: input.deviceId,
    kind: input.kind,
    order_id: input.orderId ?? null,
    reservation_id: input.reservationId ?? null,
  });
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

export async function listAuditLog(restaurantId: string, limit = 200): Promise<AuditLogRow[]> {
  return unwrap(
    await getSupabase()
      .from('audit_log')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false })
      .limit(limit),
  );
}

// ---------------------------------------------------------------------------
// Realtime
// ---------------------------------------------------------------------------

export type RealtimeTable = 'orders' | 'reservations' | 'print_jobs' | 'printer_devices';

/**
 * Subscribes to INSERT/UPDATE events for a location across the given tables.
 * Returns a channel the caller must remove on cleanup.
 */
export function subscribeToLocation(
  locationId: string,
  tables: RealtimeTable[],
  onChange: (table: RealtimeTable, eventType: 'INSERT' | 'UPDATE' | 'DELETE') => void,
): RealtimeChannel {
  let channel = getSupabase().channel(`location:${locationId}`);
  for (const table of tables) {
    channel = channel.on(
      'postgres_changes',
      { event: '*', schema: 'public', table, filter: `location_id=eq.${locationId}` },
      (payload: { eventType?: string }) => {
        const eventType =
          payload.eventType === 'INSERT' || payload.eventType === 'DELETE' ? payload.eventType : 'UPDATE';
        onChange(table, eventType);
      },
    );
  }
  return channel.subscribe();
}
